import { useEffect, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { getEngagement, listEntities, listOrganisations } from "../lib/api";
import { clientError } from "../lib/clientMessage";
import { buildDashboardModel, type AreaBar, type DashboardKpi, type DashboardModel, type Tone } from "../lib/dashboard";
import { loadPmoReport } from "../lib/pmoReportLoader";
import { shareOfScale } from "../lib/share";
import type { Organisation, Run } from "../lib/types";
import { useBreadcrumb } from "../shell/ContextHeader";

// The assessment dashboard — DESIGN_PROPOSAL_DASHBOARD 271026 V1.1, screen 5,
// first slice: one submitted PMO self-assessment as numbers and shapes, for
// the organisation's owner and for Kenneth. Read-only. Every value comes from
// the report model through lib/dashboard.ts, so this screen and the PDF
// report can never disagree. It names no person (proposal §6).
//
// On the later list (proposal §8): Open comments, Open recommendations,
// Follow up on an incomplete area, the trend since the previous assessment.

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "running"; org: Organisation; run: Run; answered: number; total: number }
  | { kind: "ready"; org: Organisation; run: Run; dash: DashboardModel };

const TONE: Record<Tone, CSSProperties> = {
  ok: { color: "var(--state-ok)" },
  warn: { color: "var(--state-warn)" },
  muted: { color: "var(--text-muted)" },
  neutral: {},
};

export function AssessmentDashboard() {
  const { orgId, runId } = useParams<{ orgId: string; runId: string }>();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!orgId || !runId) return;
    void (async () => {
      try {
        const [orgs, load] = await Promise.all([listOrganisations(), loadPmoReport(runId)]);
        const org = orgs.find((o) => o.id === orgId);
        if (!org || !load || load.run.organisation_id !== orgId) {
          setState({ kind: "error", message: "run not found, or you are not a member" });
          return;
        }
        const [engagement, entities] = await Promise.all([getEngagement(load.run.engagement_id), listEntities(orgId)]);
        const entityName = entities.find((e) => e.id === load.run.entity_id)?.name ?? "Assessed entity";
        setTrail([
          { label: "Clients & engagements", to: "/clients" },
          { label: org.name, to: `/clients/${org.id}` },
          { label: engagement?.name ?? "Engagement", to: `/clients/${org.id}/engagements/${load.run.engagement_id}` },
          { label: entityName },
          { label: load.run.title ?? "Untitled run", to: `/clients/${org.id}/runs/${load.run.id}` },
          { label: "Dashboard" },
        ]);
        if (load.kind === "not-submitted") {
          setState({ kind: "running", org, run: load.run, answered: load.answered, total: load.total });
          return;
        }
        setState({ kind: "ready", org, run: load.run, dash: buildDashboardModel(load.model, load.scale) });
      } catch (e) {
        setState({ kind: "error", message: clientError("load", e).message });
      }
    })();
  }, [orgId, runId, setTrail]);

  if (state.kind === "loading") return <p>Loading the dashboard…</p>;
  if (state.kind === "error") return <div className="error-banner" role="alert">{state.message}</div>;
  if (state.kind === "running") {
    return <RunningView org={state.org} run={state.run} answered={state.answered} total={state.total} />;
  }
  return <DashboardView org={state.org} run={state.run} dash={state.dash} />;
}

/** A running assessment shows progress only — never who is answering (proposal §4). */
export function RunningView({ org, run, answered, total }: { org: Organisation; run: Run; answered: number; total: number }) {
  const pct = total > 0 ? Math.round((100 * answered) / total) : 0;
  return (
    <>
      <h1 style={{ marginBottom: "0.25rem" }}>{run.title ?? "Untitled run"}</h1>
      <p style={{ marginTop: 0, color: "var(--text-muted)" }}>{org.name}</p>
      <div className="card" style={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
        <Ring ratio={total > 0 ? answered / total : 0} label={`${pct}%`} tone="warn" />
        <div>
          <h3 style={{ margin: "0 0 0.25rem" }}>Running</h3>
          <p style={{ margin: 0 }}>
            {answered} of {total} questions answered. The dashboard and the report are produced when the assessment is
            submitted.
          </p>
        </div>
      </div>
      <p style={{ marginTop: "1rem" }}>
        <Link className="btn btn-secondary" to={`/clients/${org.id}/runs/${run.id}`}>Back to the run</Link>
      </p>
    </>
  );
}

/** The dashboard proper: pure rendering of a DashboardModel. */
export function DashboardView({ org, run, dash }: { org: Organisation; run: Run; dash: DashboardModel }) {
  const date = new Date(dash.assessedOn).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", flexWrap: "wrap" }}>
        <h1 style={{ marginBottom: 0 }}>{run.title ?? "Untitled run"}</h1>
        <span style={{ color: "var(--text-muted)" }}>
          {org.name} · {dash.instrumentTitle} {dash.instrumentVersion} · completed {date}
        </span>
      </div>
      <div className="no-print" style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
        <Link className="btn" to={`/clients/${org.id}/runs/${run.id}/client-report?print=1`} target="_blank" rel="noopener">
          Download PDF report
        </Link>
        <Link className="btn btn-secondary" to={`/clients/${org.id}/runs/${run.id}/client-report`} target="_blank" rel="noopener">
          Open report
        </Link>
      </div>

      {dash.overall.isPartial && (
        <p className="report-stale" style={{ marginTop: "1rem" }}>
          <strong>{dash.overall.partialLabel}</strong>
        </p>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem", marginTop: "1rem" }}>
        {dash.kpis.map((k) => (
          <Kpi key={k.key} kpi={k} dash={dash} />
        ))}
      </div>

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Practice areas</h2>
        <p style={{ marginTop: 0, color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>
          Self-declared implementation level per area, as the average answer on the {dash.scale.min}–{dash.scale.max} scale.
          Focus areas are tagged; incomplete and not-applicable areas carry no bar.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr max-content", gap: "0.45rem 0.9rem", alignItems: "center" }}>
          {dash.bars.map((b) => (
            <Bar key={b.blockId} bar={b} />
          ))}
        </div>
      </section>

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Needs your attention</h2>
        {dash.attention.length === 0 ? (
          <p style={{ margin: 0 }}>Every practice area was fully assessed.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
            {dash.attention.map((a) => (
              <li key={a.blockId} style={{ marginBottom: "0.4rem" }}>
                <strong>{a.title}</strong> — {a.text}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2 style={{ marginTop: 0 }}>Suggested focus areas</h2>
        {dash.focus.length === 0 ? (
          <p style={{ margin: 0 }}>{dash.focusNote}</p>
        ) : (
          <ol style={{ margin: 0, paddingLeft: "1.2rem" }}>
            {dash.focus.map((f) => (
              <li key={f.blockId} style={{ marginBottom: "0.6rem" }}>
                <strong>{f.title}</strong> — {f.state}
                <ul style={{ margin: "0.2rem 0 0" }}>
                  {f.opportunities.map((o, i) => (
                    <li key={i}>{o}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
        {dash.focus.length > 0 && dash.focusNote && <p style={{ marginBottom: 0 }}>{dash.focusNote}</p>}
      </section>

      <footer className="report-footer">
        {dash.templateId} {dash.templateVersion} · instrument {dash.instrumentVersion} · {dash.derivation} ·{" "}
        <span style={{ fontFamily: "var(--lf-font-mono)", overflowWrap: "anywhere" }}>{dash.definitionHash}</span>
      </footer>
    </>
  );
}

function Kpi({ kpi, dash }: { kpi: DashboardKpi; dash: DashboardModel }) {
  const shape =
    kpi.key === "coverage" ? (
      <Ring ratio={dash.coverage.ratio} label={`${Math.round(dash.coverage.ratio * 100)}%`} tone={kpi.tone} />
    ) : kpi.key === "overall" && dash.overall.average !== null ? (
      <Ring ratio={shareOfScale(dash.overall.average, dash.scale) ?? 0} label={dash.overall.average.toFixed(1)} tone={kpi.tone} />
    ) : null;
  return (
    <div className="card" style={{ display: "flex", alignItems: "center", gap: "0.9rem", margin: 0 }}>
      {shape}
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {kpi.label}
        </div>
        <div style={{ fontSize: "1.45rem", fontWeight: 700, lineHeight: 1.2, ...TONE[kpi.tone] }}>{kpi.value}</div>
        {kpi.detail && <div style={{ color: "var(--text-muted)", fontSize: "var(--lf-body-size)" }}>{kpi.detail}</div>}
      </div>
    </div>
  );
}

// A ring: the one shape Kenneth asked for on the KPI cards (proposal §1). It
// draws a ratio the model already holds; it computes nothing.
function Ring({ ratio, label, tone }: { ratio: number; label: string; tone: Tone }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, ratio));
  const stroke = tone === "warn" ? "var(--state-warn)" : tone === "ok" ? "var(--state-ok)" : "var(--accent)";
  return (
    <svg width="60" height="60" viewBox="0 0 60 60" role="img" aria-label={label}>
      <circle cx="30" cy="30" r={r} fill="none" stroke="var(--border)" strokeWidth="7" />
      <circle
        cx="30" cy="30" r={r} fill="none" stroke={stroke} strokeWidth="7" strokeLinecap="round"
        strokeDasharray={`${clamped * c} ${c}`} transform="rotate(-90 30 30)"
      />
      <text x="30" y="34" textAnchor="middle" fontSize="12" fontWeight="700" fill="currentColor">{label}</text>
    </svg>
  );
}

function Bar({ bar: b }: { bar: AreaBar }) {
  const right =
    b.status === "complete"
      ? `${b.state} · ${b.average!.toFixed(2)}`
      : b.status === "incomplete"
        ? `Incomplete · ${b.assessed} of ${b.applicable}`
        : "Not applicable";
  return (
    <>
      <div style={{ fontSize: "var(--lf-body-size)" }}>
        {b.sequence}. {b.title}
        {b.focusRank !== null && (
          <span
            style={{ marginLeft: "0.4rem", fontSize: "var(--lf-meta-size)", padding: "0 0.4rem", borderRadius: "999px", background: "var(--state-warn-soft)", color: "var(--state-warn)" }}
          >
            focus {b.focusRank}
          </span>
        )}
      </div>
      <div style={{ height: "10px", background: "var(--lf-muted-40, var(--border))", borderRadius: "5px", overflow: "hidden" }} aria-hidden="true">
        {b.ratio !== null && (
          <div style={{ width: `${Math.round(b.ratio * 100)}%`, height: "100%", background: b.focusRank !== null ? "var(--state-warn)" : "var(--accent)" }} />
        )}
        {b.status === "incomplete" && (
          <div style={{ width: "100%", height: "100%", backgroundImage: "repeating-linear-gradient(45deg, var(--state-warn-soft) 0 6px, transparent 6px 12px)" }} />
        )}
      </div>
      <div style={{ fontSize: "var(--lf-body-size)", color: b.status === "complete" ? "inherit" : "var(--text-muted)", whiteSpace: "nowrap" }}>
        {right}
      </div>
    </>
  );
}
